import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

export default function SiteDetail() {
  const {id} = useParams();
  const [site, setSite] = useState(null);
  const [stats, setStats] = useState(null);
  const [pageviews, setPageviews] = useState([]);
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateMode, setDateMode] = useState('preset');
  const [showSnippet, setShowSnippet] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [realtime, setRealtime] = useState(null);
  const [goals, setGoals] = useState([]);
  const [goalName, setGoalName] = useState('');
  const [goalPath, setGoalPath] = useState('');
  const [goalValue, setGoalValue] = useState('');
  const [goalCreating, setGoalCreating] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalStats, setGoalStats] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [annoDate, setAnnoDate] = useState('');
  const [annoText, setAnnoText] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [alertForm, setAlertForm] = useState({name: '', metric: 'pageviews', conditionType: 'above', threshold: ''});
  const [funnels, setFunnels] = useState([]);
  const [funnelName, setFunnelName] = useState('');
  const [funnelSteps, setFunnelSteps] = useState([{name: '', path: ''}, {name: '', path: ''}]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [funnelStats, setFunnelStats] = useState(null);
  const [userFlow, setUserFlow] = useState(null);
  const [showFlow, setShowFlow] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [cohortData, setCohortData] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [clickPath, setClickPath] = useState('');
  const [experiments, setExperiments] = useState([]);
  const [expName, setExpName] = useState('');
  const [expPath, setExpPath] = useState('');
  const [expVariants, setExpVariants] = useState([{name: 'Control', weight: 50}, {name: 'Variant B', weight: 50}]);
  const [selectedExp, setSelectedExp] = useState(null);
  const [expStats, setExpStats] = useState(null);
  const [uptimeChecks, setUptimeChecks] = useState([]);
  const [uptimeUrl, setUptimeUrl] = useState('');
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [uptimeStats, setUptimeStats] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [segments, setSegments] = useState([]);
  const [segName, setSegName] = useState('');
  const [segFilters, setSegFilters] = useState([{field: 'browser', operator: 'equals', value: ''}]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segStats, setSegStats] = useState(null);

  const dateParams = dateMode === 'custom' && startDate && endDate
    ? `startDate=${startDate}&endDate=${endDate}` : `days=${days}`;

  const load = useCallback(() => {
    api(`/analytics/sites/${id}`).then(r => r.ok ? r.json() : null).then(setSite);
    api(`/analytics/sites/${id}/stats?${dateParams}`).then(r => r.ok ? r.json() : null).then(setStats);
    api(`/analytics/sites/${id}/pageviews?${dateParams}`).then(r => r.ok ? r.json() : []).then(setPageviews);
    api(`/analytics/sites/${id}/goals`).then(r => r.ok ? r.json() : []).then(setGoals);
    api(`/analytics/sites/${id}/events?${dateParams}`).then(r => r.ok ? r.json() : null).then(setEventStats);
    api(`/analytics/sites/${id}/annotations`).then(r => r.ok ? r.json() : []).then(setAnnotations);
    api(`/analytics/sites/${id}/alerts`).then(r => r.ok ? r.json() : []).then(setAlerts);
    api(`/analytics/sites/${id}/funnels`).then(r => r.ok ? r.json() : []).then(setFunnels);
    api(`/analytics/sites/${id}/experiments`).then(r => r.ok ? r.json() : []).then(setExperiments);
    api(`/analytics/sites/${id}/uptime-checks`).then(r => r.ok ? r.json() : []).then(setUptimeChecks);
    api(`/analytics/sites/${id}/segments`).then(r => r.ok ? r.json() : []).then(setSegments);
  }, [id, dateParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) return;
    const fetchRealtime = () => api(`/analytics/sites/${id}/realtime`).then(r => r.ok ? r.json() : null).then(setRealtime);
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (!selectedGoal) { setGoalStats(null); return; }
    api(`/analytics/sites/${id}/goals/${selectedGoal}/stats?${dateParams}`).then(r => r.ok ? r.json() : null).then(setGoalStats);
  }, [id, selectedGoal, dateParams]);

  useEffect(() => {
    if (!selectedExp) { setExpStats(null); return; }
    api(`/analytics/sites/${id}/experiments/${selectedExp}/stats?days=${days}`).then(r => r.ok ? r.json() : null).then(setExpStats);
  }, [id, selectedExp, days]);

  useEffect(() => {
    if (!selectedCheck) { setUptimeStats(null); return; }
    api(`/analytics/sites/${id}/uptime-checks/${selectedCheck}/stats?days=${days}`).then(r => r.ok ? r.json() : null).then(setUptimeStats);
  }, [id, selectedCheck, days]);

  useEffect(() => {
    if (!selectedSegment) { setSegStats(null); return; }
    api(`/analytics/sites/${id}/segments/${selectedSegment}/stats?days=${days}`).then(r => r.ok ? r.json() : null).then(setSegStats);
  }, [id, selectedSegment, days]);

  useEffect(() => {
    if (!selectedFunnel) { setFunnelStats(null); return; }
    api(`/analytics/sites/${id}/funnels/${selectedFunnel}/stats?days=${days}`).then(r => r.ok ? r.json() : null).then(setFunnelStats);
  }, [id, selectedFunnel, days]);

  useEffect(() => {
    if (!showFlow) { setUserFlow(null); return; }
    api(`/analytics/sites/${id}/flow?days=${days}`).then(r => r.ok ? r.json() : null).then(setUserFlow);
  }, [id, showFlow, days]);

  const snippet = site ? `<script>
(function(){var s="${site.trackingId}",u="https://www.matterking.com/api/analytics/collect";
var v=document.cookie.match(/mk_vid=([^;]+)/);if(!v){v=crypto.randomUUID();document.cookie="mk_vid="+v+";path=/;max-age=31536000;SameSite=Lax";}else{v=v[1];}
var p=new URLSearchParams(location.search),d={siteId:s,path:location.pathname,referrer:document.referrer,visitorId:v};
if(p.get("utm_source"))d.utmSource=p.get("utm_source");
if(p.get("utm_medium"))d.utmMedium=p.get("utm_medium");
if(p.get("utm_campaign"))d.utmCampaign=p.get("utm_campaign");
if(window.performance&&performance.timing){var lt=performance.timing.loadEventEnd-performance.timing.navigationStart;if(lt>0)d.loadTimeMs=lt;}
if(window.PerformanceObserver){try{new PerformanceObserver(function(l){l.getEntries().forEach(function(e){if(e.entryType==="largest-contentful-paint")d.lcp=e.startTime;if(e.entryType==="first-input")d.fid=e.processingStart-e.startTime;if(e.entryType==="layout-shift"&&!e.hadRecentInput)d.cls=(d.cls||0)+e.value;});}).observe({type:"largest-contentful-paint",buffered:true});new PerformanceObserver(function(l){l.getEntries().forEach(function(e){d.fid=e.processingStart-e.startTime;});}).observe({type:"first-input",buffered:true});new PerformanceObserver(function(l){l.getEntries().forEach(function(e){if(!e.hadRecentInput)d.cls=(d.cls||0)+e.value;});}).observe({type:"layout-shift",buffered:true});}catch(e){}}
setTimeout(function(){fetch(u,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(d),keepalive:true});},3000);
document.addEventListener("click",function(e){var t=e.target,r=t.getBoundingClientRect(),vw=window.innerWidth,vh=window.innerHeight;
fetch(u+"/click",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({siteId:s,path:location.pathname,visitorId:v,elementTag:t.tagName,elementText:(t.innerText||"").substring(0,100),elementSelector:t.id?"#"+t.id:t.className?"."+(t.className.split(" ")[0]):"",xPct:(r.left+r.width/2)/vw,yPct:(r.top+r.height/2+window.scrollY)/(document.body.scrollHeight||vh),viewportWidth:vw,viewportHeight:vh}),keepalive:true});
});
})();
</script>` : '';

  const eventSnippet = site ? `// Track custom events with optional revenue
window.mkTrack = function(name, category, value, revenue) {
  var v = document.cookie.match(/mk_vid=([^;]+)/);
  fetch("https://www.matterking.com/api/analytics/collect/event", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({siteId: "${site.trackingId}", name: name, category: category || "", value: value || "", path: location.pathname, visitorId: v ? v[1] : "", revenue: revenue || null}),
    keepalive: true
  });
};
// Usage: mkTrack("purchase", "ecommerce", "plan_pro", 49.99)` : '';

  if (!site) return <Layout><div className="text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{site.name}</h2>
            {site.domain && <p className="text-sm text-gray-500">{site.domain}</p>}
          </div>
          <button onClick={() => { setEditName(site.name); setEditDomain(site.domain || ''); setShowEdit(true); }}
            className="text-gray-400 hover:text-amber-600 p-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={dateMode === 'custom' ? 'custom' : days} onChange={e => {
            if (e.target.value === 'custom') { setDateMode('custom'); }
            else { setDateMode('preset'); setDays(Number(e.target.value)); }
          }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value="custom">Custom range</option>
          </select>
          {dateMode === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </>
          )}
          <button onClick={() => setShowSnippet(!showSnippet)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">
            Tracking Code
          </button>
          <button onClick={async () => {
            const res = await api(`/analytics/sites/${id}/export?${dateParams}`);
            if (!res.ok) return;
            const csv = await res.text();
            const blob = new Blob([csv], {type: 'text/csv'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'pageviews.csv'; a.click();
            URL.revokeObjectURL(url);
          }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
            Export CSV
          </button>
        </div>
      </div>

      {realtime != null && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-2">
          <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-sm font-medium text-green-800">{realtime.activeVisitors} active visitor{realtime.activeVisitors !== 1 ? 's' : ''} right now</span>
        </div>
      )}

      {showSnippet && (
        <div className="bg-gray-900 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Add this before &lt;/body&gt; on your website</span>
            <button onClick={() => { navigator.clipboard.writeText(snippet); }}
              className="text-xs text-amber-400 hover:text-amber-300">Copy</button>
          </div>
          <pre className="text-sm text-green-400 overflow-x-auto whitespace-pre-wrap">{snippet}</pre>
          <div className="mt-4 border-t border-gray-700 pt-3">
            <span className="text-sm text-gray-400">Event tracking (add after the snippet above)</span>
            <pre className="text-sm text-blue-400 overflow-x-auto whitespace-pre-wrap mt-2">{eventSnippet}</pre>
          </div>
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <StatCard label="Pageviews" value={stats.totalPageviews} prev={stats.prevTotalPageviews} />
            <StatCard label="Unique Visitors" value={stats.uniqueVisitors} prev={stats.prevUniqueVisitors} />
            <StatCard label="Phone Calls" value={stats.totalCalls} prev={stats.prevTotalCalls} />
            <StatCard label="Bounce Rate" value={stats.bounceRate != null ? `${stats.bounceRate}%` : '—'} />
            <StatCard label="Avg Session" value={formatDuration(stats.avgSessionDurationSeconds)} />
            <StatCard label="Pages / Session" value={stats.avgPagesPerSession != null ? stats.avgPagesPerSession : '—'} />
          </div>

          <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
            {['overview','events','heatmap','experiments','uptime','segments','funnels','flow','cohorts','annotations','alerts'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${activeTab === tab ? 'border-amber-600 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <>

          {stats.pageviewsByDay && stats.pageviewsByDay.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Pageviews Over Time</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.pageviewsByDay}>
                  <XAxis dataKey="date" tick={{fontSize: 12}} />
                  <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#d97706" radius={[4, 4, 0, 0]} />
                  {annotations.map(a => (
                    <ReferenceLine key={a.id} x={a.annotationDate} stroke="#6366f1" strokeDasharray="3 3"
                      label={{value: a.text, position: 'top', fontSize: 10, fill: '#6366f1'}} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {stats.pageviewsByPage && stats.pageviewsByPage.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Page Trends</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={buildPageTrendData(stats.pageviewsByPage)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{fontSize: 12}} />
                  <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                  <Tooltip />
                  <Legend />
                  {stats.pageviewsByPage.map((pt, i) => (
                    <Line key={pt.page} type="monotone" dataKey={pt.page} stroke={PAGE_COLORS[i % PAGE_COLORS.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <StatTable title="Top Pages" items={stats.topPages} />
            <StatTable title="Top Referrers" items={stats.topReferrers} />
            <StatTable title="Countries" items={stats.topCountries} />
            <StatTable title="Cities" items={stats.topCities} />
            <StatTable title="Referrer Domains" items={stats.topReferrerDomains} />
            <StatTable title="Campaigns" items={stats.topCampaigns} />
            <StatTable title="Browsers" items={stats.topBrowsers} />
            <StatTable title="Operating Systems" items={stats.topOs} />
            <StatTable title="Devices" items={stats.topDevices} />
            <StatTable title="Entry Pages" items={stats.topEntryPages} />
            <StatTable title="Exit Pages" items={stats.topExitPages} />
          </div>

          {(stats.avgLoadTimeMs || stats.loadTimeByPage?.length > 0 || stats.avgCls != null || stats.avgLcp != null) && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Page Performance</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.avgLoadTimeMs && (
                  <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">Avg Load Time</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.avgLoadTimeMs}ms</p>
                  </div>
                )}
                {stats.avgLcp != null && (
                  <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">LCP</p>
                    <p className={`text-2xl font-bold ${stats.avgLcp <= 2500 ? 'text-green-600' : stats.avgLcp <= 4000 ? 'text-amber-600' : 'text-red-600'}`}>{Math.round(stats.avgLcp)}ms</p>
                    <p className="text-xs text-gray-400">{stats.avgLcp <= 2500 ? 'Good' : stats.avgLcp <= 4000 ? 'Needs Improvement' : 'Poor'}</p>
                  </div>
                )}
                {stats.avgFid != null && (
                  <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">FID</p>
                    <p className={`text-2xl font-bold ${stats.avgFid <= 100 ? 'text-green-600' : stats.avgFid <= 300 ? 'text-amber-600' : 'text-red-600'}`}>{Math.round(stats.avgFid)}ms</p>
                    <p className="text-xs text-gray-400">{stats.avgFid <= 100 ? 'Good' : stats.avgFid <= 300 ? 'Needs Improvement' : 'Poor'}</p>
                  </div>
                )}
                {stats.avgCls != null && (
                  <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">CLS</p>
                    <p className={`text-2xl font-bold ${stats.avgCls <= 0.1 ? 'text-green-600' : stats.avgCls <= 0.25 ? 'text-amber-600' : 'text-red-600'}`}>{stats.avgCls}</p>
                    <p className="text-xs text-gray-400">{stats.avgCls <= 0.1 ? 'Good' : stats.avgCls <= 0.25 ? 'Needs Improvement' : 'Poor'}</p>
                  </div>
                )}
              </div>
              {stats.loadTimeByPage?.length > 0 && (
                <div className="mt-4">
                  <StatTable title="Load Time by Page (avg ms)" items={stats.loadTimeByPage} />
                </div>
              )}
            </div>
          )}

          {/* Visitor Sessions */}
          <div className="mt-6">
            <button onClick={() => setShowSessions(!showSessions)}
              className="text-sm text-amber-600 hover:text-amber-800 font-medium mb-4">
              {showSessions ? '▼ Hide Visitor Sessions' : '▶ Show Visitor Sessions'}
            </button>
            {showSessions && <VisitorSessions pageviews={pageviews} />}
          </div>

          {/* Goals */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Goals / Conversions</h3>
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <form onSubmit={async e => {
                e.preventDefault(); if (!goalName.trim() || !goalPath.trim()) return;
                setGoalCreating(true);
                const body = {name: goalName.trim(), targetPath: goalPath.trim()};
                if (goalValue) body.goalValue = parseFloat(goalValue);
                const res = await api(`/analytics/sites/${id}/goals`, {method: 'POST', body: JSON.stringify(body)});
                if (res.ok) { setGoalName(''); setGoalPath(''); setGoalValue(''); load(); }
                setGoalCreating(false);
              }} className="flex flex-col sm:flex-row gap-2">
                <input type="text" required placeholder="Goal name" value={goalName} onChange={e => setGoalName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm" />
                <input type="text" required placeholder="/thank-you" value={goalPath} onChange={e => setGoalPath(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm" />
                <input type="number" step="0.01" placeholder="Value ($)" value={goalValue} onChange={e => setGoalValue(e.target.value)}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm" />
                <button type="submit" disabled={goalCreating} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm">
                  {goalCreating ? 'Adding...' : 'Add Goal'}
                </button>
              </form>
            </div>
            {goals.length > 0 && (
              <div className="space-y-2 mb-4">
                {goals.map(g => (
                  <div key={g.id} className={`bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition ${selectedGoal === g.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setSelectedGoal(selectedGoal === g.id ? null : g.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{g.name}</span>
                      <span className="text-sm text-gray-500 ml-2">{g.targetPath}</span>
                      {g.goalValue && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded ml-2">${g.goalValue}</span>}
                    </div>
                    <button onClick={async e => { e.stopPropagation(); await api(`/analytics/sites/${id}/goals/${g.id}`, {method: 'DELETE'}); if (selectedGoal === g.id) setSelectedGoal(null); load(); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </div>
                ))}
              </div>
            )}
            {goalStats && selectedGoal && (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <StatCard label="Conversions" value={goalStats.conversions} prev={goalStats.prevConversions} />
                  <StatCard label="Unique Visitors" value={goalStats.uniqueVisitors} />
                  <StatCard label="Conversion Rate" value={goalStats.conversionRate != null ? `${goalStats.conversionRate}%` : '—'} />
                  {goalStats.totalRevenue != null && <StatCard label="Revenue" value={`$${goalStats.totalRevenue.toFixed(2)}`} />}
                </div>
                {goalStats.conversionsByDay && goalStats.conversionsByDay.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Conversions Over Time</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={goalStats.conversionsByDay}>
                        <XAxis dataKey="date" tick={{fontSize: 12}} />
                        <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="grid md:grid-cols-3 gap-4">
                  <StatTable title="By Campaign" items={goalStats.byCampaign} />
                  <StatTable title="By Referrer" items={goalStats.byReferrer} />
                  <StatTable title="By Device" items={goalStats.byDevice} />
                </div>
              </div>
            )}
          </div>
            </>
          )}

          {activeTab === 'events' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Events</h3>
              {eventStats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <StatCard label="Total Events" value={eventStats.totalEvents} />
                    {eventStats.totalRevenue != null && <StatCard label="Event Revenue" value={`$${eventStats.totalRevenue.toFixed(2)}`} />}
                  </div>
                  {eventStats.eventsByDay && eventStats.eventsByDay.length > 0 && (
                    <div className="bg-white rounded-lg shadow p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-500 mb-4">Events Over Time</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={eventStats.eventsByDay}>
                          <XAxis dataKey="date" tick={{fontSize: 12}} />
                          <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                    <StatTable title="Top Events" items={eventStats.topEvents} />
                    <StatTable title="Top Categories" items={eventStats.topCategories} />
                  </div>
                </>
              )}
              {(!eventStats || eventStats.totalEvents === 0) && (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  <p className="mb-2">No events recorded yet.</p>
                  <p className="text-sm">Add the event tracking snippet to your site and call <code className="bg-gray-100 px-1 rounded">mkTrack("event_name")</code></p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'heatmap' && (
            <HeatmapTab siteId={id} days={days} clickStats={clickStats} setClickStats={setClickStats} clickPath={clickPath} setClickPath={setClickPath} topPages={stats?.topPages} />
          )}

          {activeTab === 'experiments' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">A/B Testing</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="flex flex-col gap-2 mb-3">
                  <div className="flex gap-2">
                    <input type="text" placeholder="Experiment name" value={expName} onChange={e => setExpName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    <input type="text" placeholder="/target-path" value={expPath} onChange={e => setExpPath(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  {expVariants.map((v, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input type="text" placeholder={`Variant ${i+1}`} value={v.name} onChange={e => { const nv = [...expVariants]; nv[i] = {...nv[i], name: e.target.value}; setExpVariants(nv); }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="number" placeholder="Weight" value={v.weight} onChange={e => { const nv = [...expVariants]; nv[i] = {...nv[i], weight: parseInt(e.target.value) || 50}; setExpVariants(nv); }}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      {expVariants.length > 2 && <button onClick={() => setExpVariants(expVariants.filter((_, j) => j !== i))} className="text-red-500 text-sm">✕</button>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setExpVariants([...expVariants, {name: '', weight: 50}])} className="text-sm text-amber-600 hover:text-amber-800">+ Add Variant</button>
                  <button onClick={async () => {
                    if (!expName.trim() || !expPath.trim()) return;
                    await api(`/analytics/sites/${id}/experiments`, {method: 'POST', body: JSON.stringify({name: expName.trim(), targetPath: expPath.trim(), variants: expVariants})});
                    setExpName(''); setExpPath(''); setExpVariants([{name: 'Control', weight: 50}, {name: 'Variant B', weight: 50}]); load();
                  }} className="px-4 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm ml-auto">Create Experiment</button>
                </div>
              </div>
              {experiments.map(exp => (
                <div key={exp.id} className="mb-4">
                  <div className={`bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition ${selectedExp === exp.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setSelectedExp(selectedExp === exp.id ? null : exp.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{exp.name}</span>
                      <span className="text-sm text-gray-500 ml-2">{exp.targetPath}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ml-2 ${exp.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{exp.status}</span>
                      <span className="text-xs text-gray-400 ml-2">{exp.variants?.length || 0} variants</span>
                    </div>
                    <button onClick={async e => { e.stopPropagation(); await api(`/analytics/sites/${id}/experiments/${exp.id}`, {method: 'DELETE'}); if (selectedExp === exp.id) setSelectedExp(null); load(); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </div>
                  {selectedExp === exp.id && expStats && (
                    <div className="bg-white rounded-lg shadow p-4 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <StatCard label="Total Visitors" value={expStats.totalVisitors} />
                        {expStats.winner && <div className="bg-green-50 rounded-lg p-4"><p className="text-sm text-green-600">Winner</p><p className="text-lg font-bold text-green-800">{expStats.winner}</p></div>}
                      </div>
                      <div className="space-y-3">
                        {expStats.variants?.map(v => (
                          <div key={v.variantId} className="flex items-center gap-4">
                            <span className="text-sm font-medium text-gray-700 w-28">{v.name}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-8 relative">
                              <div className="bg-amber-500 rounded-full h-8 flex items-center justify-end pr-3"
                                style={{width: `${Math.max(expStats.totalVisitors > 0 ? (v.visitors / expStats.totalVisitors) * 100 : 0, 5)}%`}}>
                                <span className="text-xs text-white font-medium">{v.visitors} visitors</span>
                              </div>
                            </div>
                            <div className="text-right w-32">
                              <span className="text-sm font-medium text-gray-900">{v.conversions} conv</span>
                              <span className="text-xs text-gray-500 ml-1">({v.conversionRate}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {experiments.length === 0 && <p className="text-gray-500 text-sm">No experiments yet. Create one to start A/B testing.</p>}
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Integration</h4>
                <pre className="text-xs text-gray-600 bg-white rounded p-3 overflow-x-auto">{`// Add after your tracking snippet
fetch("https://www.matterking.com/api/analytics/collect/assign", {
  method: "POST",
  headers: {"Content-Type": "application/json"},
  body: JSON.stringify({siteId: "${id}", experimentId: "EXPERIMENT_ID", visitorId: v})
}).then(r => r.json()).then(data => {
  // data.variantName = "Control" or "Variant B"
  // Show different content based on variant
});`}</pre>
              </div>
            </div>
          )}

          {activeTab === 'uptime' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Uptime Monitoring</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <form onSubmit={async e => {
                  e.preventDefault(); if (!uptimeUrl.trim()) return;
                  await api(`/analytics/sites/${id}/uptime-checks`, {method: 'POST', body: JSON.stringify({url: uptimeUrl.trim()})});
                  setUptimeUrl(''); load();
                }} className="flex gap-2">
                  <input type="url" required placeholder="https://example.com" value={uptimeUrl} onChange={e => setUptimeUrl(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Add Check</button>
                </form>
              </div>
              {uptimeChecks.map(c => (
                <div key={c.id} className="mb-4">
                  <div className={`bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition ${selectedCheck === c.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setSelectedCheck(selectedCheck === c.id ? null : c.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{c.url}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ml-2 ${c.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{c.enabled ? 'Active' : 'Paused'}</span>
                    </div>
                    <button onClick={async e => { e.stopPropagation(); await api(`/analytics/sites/${id}/uptime-checks/${c.id}`, {method: 'DELETE'}); if (selectedCheck === c.id) setSelectedCheck(null); load(); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </div>
                  {selectedCheck === c.id && uptimeStats && (
                    <div className="bg-white rounded-lg shadow p-4 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-white border rounded-lg p-4">
                          <p className="text-sm text-gray-500">Availability</p>
                          <p className={`text-2xl font-bold ${uptimeStats.availabilityPct >= 99.5 ? 'text-green-600' : uptimeStats.availabilityPct >= 95 ? 'text-amber-600' : 'text-red-600'}`}>{uptimeStats.availabilityPct}%</p>
                        </div>
                        <StatCard label="Avg Response" value={`${uptimeStats.avgResponseTimeMs}ms`} />
                        <StatCard label="Total Checks" value={uptimeStats.totalChecks} />
                        <StatCard label="Downtime Events" value={uptimeStats.totalDowntime} />
                      </div>
                      {uptimeStats.results?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-2">Recent Results</h4>
                          <div className="flex gap-0.5 flex-wrap mb-3">
                            {uptimeStats.results.slice(0, 50).reverse().map((r, i) => (
                              <div key={i} className={`w-3 h-8 rounded-sm ${r.isUp ? 'bg-green-400' : 'bg-red-400'}`}
                                title={`${r.isUp ? 'Up' : 'Down'} - ${r.responseTimeMs || 0}ms - ${new Date(r.createdAt).toLocaleString()}`} />
                            ))}
                          </div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {uptimeStats.results.slice(0, 20).map((r, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <span className={`h-2 w-2 rounded-full ${r.isUp ? 'bg-green-500' : 'bg-red-500'}`} />
                                  <span className="text-gray-500">{new Date(r.createdAt).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  {r.statusCode && <span className="text-gray-400">{r.statusCode}</span>}
                                  {r.responseTimeMs && <span className="text-gray-600">{r.responseTimeMs}ms</span>}
                                  {r.errorMessage && <span className="text-red-500 truncate max-w-xs">{r.errorMessage}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {uptimeChecks.length === 0 && <p className="text-gray-500 text-sm">No uptime checks configured. Checks run every 5 minutes.</p>}
            </div>
          )}

          {activeTab === 'segments' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Audience Segments</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="flex flex-col gap-2 mb-3">
                  <input type="text" placeholder="Segment name" value={segName} onChange={e => setSegName(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  {segFilters.map((f, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <select value={f.field} onChange={e => { const nf = [...segFilters]; nf[i] = {...nf[i], field: e.target.value}; setSegFilters(nf); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        {['browser','os','device','country','city','path','referrer','utm_source','utm_medium','utm_campaign'].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <select value={f.operator} onChange={e => { const nf = [...segFilters]; nf[i] = {...nf[i], operator: e.target.value}; setSegFilters(nf); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="equals">equals</option>
                        <option value="contains">contains</option>
                        <option value="not_equals">not equals</option>
                        <option value="starts_with">starts with</option>
                      </select>
                      <input type="text" placeholder="Value" value={f.value} onChange={e => { const nf = [...segFilters]; nf[i] = {...nf[i], value: e.target.value}; setSegFilters(nf); }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      {segFilters.length > 1 && <button onClick={() => setSegFilters(segFilters.filter((_, j) => j !== i))} className="text-red-500 text-sm">✕</button>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSegFilters([...segFilters, {field: 'browser', operator: 'equals', value: ''}])} className="text-sm text-amber-600 hover:text-amber-800">+ Add Filter</button>
                  <button onClick={async () => {
                    if (!segName.trim() || segFilters.some(f => !f.value.trim())) return;
                    await api(`/analytics/sites/${id}/segments`, {method: 'POST', body: JSON.stringify({name: segName.trim(), filters: segFilters})});
                    setSegName(''); setSegFilters([{field: 'browser', operator: 'equals', value: ''}]); load();
                  }} className="px-4 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm ml-auto">Create Segment</button>
                </div>
              </div>
              {segments.map(seg => (
                <div key={seg.id} className="mb-4">
                  <div className={`bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition ${selectedSegment === seg.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setSelectedSegment(selectedSegment === seg.id ? null : seg.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{seg.name}</span>
                      <span className="text-xs text-gray-400 ml-2">
                        {seg.filters?.map(f => `${f.field} ${f.operator} "${f.value}"`).join(' AND ')}
                      </span>
                    </div>
                    <button onClick={async e => { e.stopPropagation(); await api(`/analytics/sites/${id}/segments/${seg.id}`, {method: 'DELETE'}); if (selectedSegment === seg.id) setSelectedSegment(null); load(); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </div>
                  {selectedSegment === seg.id && segStats && (
                    <div className="bg-white rounded-lg shadow p-4 mt-2">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <StatCard label="Pageviews" value={segStats.totalPageviews} />
                        <StatCard label="Unique Visitors" value={segStats.uniqueVisitors} />
                        <StatCard label="Bounce Rate" value={segStats.bounceRate != null ? `${segStats.bounceRate}%` : '—'} />
                      </div>
                      {segStats.pageviewsByDay?.length > 0 && (
                        <div className="mb-4">
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={segStats.pageviewsByDay}>
                              <XAxis dataKey="date" tick={{fontSize: 12}} />
                              <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 gap-4">
                        <StatTable title="Top Pages" items={segStats.topPages} />
                        <StatTable title="Top Referrers" items={segStats.topReferrers} />
                        <StatTable title="Browsers" items={segStats.topBrowsers} />
                        <StatTable title="Devices" items={segStats.topDevices} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {segments.length === 0 && <p className="text-gray-500 text-sm">No segments defined. Create one to filter analytics by audience criteria.</p>}
            </div>
          )}

          {activeTab === 'funnels' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Funnels</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="flex flex-col gap-2 mb-3">
                  <input type="text" placeholder="Funnel name" value={funnelName} onChange={e => setFunnelName(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  {funnelSteps.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" placeholder={`Step ${i+1} name`} value={s.name} onChange={e => { const ns = [...funnelSteps]; ns[i] = {...ns[i], name: e.target.value}; setFunnelSteps(ns); }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="text" placeholder="/path" value={s.path} onChange={e => { const ns = [...funnelSteps]; ns[i] = {...ns[i], path: e.target.value}; setFunnelSteps(ns); }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      {funnelSteps.length > 2 && <button onClick={() => setFunnelSteps(funnelSteps.filter((_, j) => j !== i))} className="text-red-500 text-sm">✕</button>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setFunnelSteps([...funnelSteps, {name: '', path: ''}])} className="text-sm text-amber-600 hover:text-amber-800">+ Add Step</button>
                  <button onClick={async () => {
                    if (!funnelName.trim() || funnelSteps.some(s => !s.name.trim() || !s.path.trim())) return;
                    await api(`/analytics/sites/${id}/funnels`, {method: 'POST', body: JSON.stringify({name: funnelName.trim(), steps: funnelSteps})});
                    setFunnelName(''); setFunnelSteps([{name: '', path: ''}, {name: '', path: ''}]); load();
                  }} className="px-4 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm ml-auto">Create Funnel</button>
                </div>
              </div>
              {funnels.map(f => (
                <div key={f.id} className="mb-4">
                  <div className={`bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between cursor-pointer hover:shadow-md transition ${selectedFunnel === f.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setSelectedFunnel(selectedFunnel === f.id ? null : f.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{f.name}</span>
                      <span className="text-sm text-gray-500 ml-2">{f.steps?.length || 0} steps</span>
                    </div>
                    <button onClick={async e => { e.stopPropagation(); await api(`/analytics/sites/${id}/funnels/${f.id}`, {method: 'DELETE'}); if (selectedFunnel === f.id) setSelectedFunnel(null); load(); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </div>
                  {selectedFunnel === f.id && funnelStats && (
                    <div className="bg-white rounded-lg shadow p-4 mt-2">
                      <div className="flex items-end gap-1" style={{height: 200}}>
                        {funnelStats.steps?.map((step, i) => {
                          const maxV = funnelStats.steps[0]?.visitors || 1;
                          const pct = maxV > 0 ? (step.visitors / maxV) * 100 : 0;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center">
                              <span className="text-xs text-gray-500 mb-1">{step.visitors}</span>
                              <div className="w-full bg-amber-500 rounded-t" style={{height: `${Math.max(pct, 2)}%`}} />
                              <span className="text-xs text-gray-700 mt-1 text-center truncate w-full">{step.name}</span>
                              {i > 0 && <span className="text-xs text-red-500">↓{step.dropoff}%</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'flow' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">User Flow</h3>
              {!showFlow ? (
                <button onClick={() => setShowFlow(true)} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Load User Flow</button>
              ) : userFlow ? (
                <div className="bg-white rounded-lg shadow p-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">Page Navigation Paths (Top 15 pages)</h4>
                  <div className="space-y-2 mb-6">
                    {userFlow.nodes?.map(n => (
                      <div key={n.id} className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 w-40 truncate">{n.name}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-5">
                          <div className="bg-amber-500 rounded-full h-5 flex items-center justify-end pr-2" style={{width: `${Math.max((n.value / (userFlow.nodes[0]?.value || 1)) * 100, 5)}%`}}>
                            <span className="text-xs text-white font-medium">{n.value}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Top Navigation Paths</h4>
                  <div className="space-y-1">
                    {userFlow.links?.slice(0, 20).map((l, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{l.source}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{l.target}</span>
                        <span className="text-gray-500 ml-auto">{l.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="text-gray-500">Loading...</div>}
            </div>
          )}

          {activeTab === 'cohorts' && (
            <CohortTab siteId={id} days={days} cohortData={cohortData} setCohortData={setCohortData} />
          )}

          {activeTab === 'annotations' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Annotations</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <form onSubmit={async e => {
                  e.preventDefault(); if (!annoDate || !annoText.trim()) return;
                  await api(`/analytics/sites/${id}/annotations`, {method: 'POST', body: JSON.stringify({annotationDate: annoDate, text: annoText.trim()})});
                  setAnnoDate(''); setAnnoText(''); load();
                }} className="flex flex-col sm:flex-row gap-2">
                  <input type="date" required value={annoDate} onChange={e => setAnnoDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <input type="text" required placeholder="e.g. Launched new campaign" value={annoText} onChange={e => setAnnoText(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Add</button>
                </form>
              </div>
              {annotations.length > 0 ? (
                <div className="bg-white rounded-lg shadow divide-y">
                  {annotations.map(a => (
                    <div key={a.id} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{a.annotationDate}</span>
                        <span className="text-sm text-gray-600 ml-3">{a.text}</span>
                      </div>
                      <button onClick={async () => { await api(`/analytics/sites/${id}/annotations/${a.id}`, {method: 'DELETE'}); load(); }}
                        className="text-sm text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-sm">No annotations yet.</p>}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts</h3>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <form onSubmit={async e => {
                  e.preventDefault(); if (!alertForm.name.trim() || !alertForm.threshold) return;
                  await api(`/analytics/sites/${id}/alerts`, {method: 'POST', body: JSON.stringify(alertForm)});
                  setAlertForm({name: '', metric: 'pageviews', conditionType: 'above', threshold: ''}); load();
                }} className="flex flex-col sm:flex-row gap-2">
                  <input type="text" required placeholder="Alert name" value={alertForm.name} onChange={e => setAlertForm({...alertForm, name: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <select value={alertForm.metric} onChange={e => setAlertForm({...alertForm, metric: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="pageviews">Pageviews</option>
                    <option value="visitors">Visitors</option>
                    <option value="bounce_rate">Bounce Rate</option>
                  </select>
                  <select value={alertForm.conditionType} onChange={e => setAlertForm({...alertForm, conditionType: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="above">Above</option>
                    <option value="below">Below</option>
                  </select>
                  <input type="number" required placeholder="Threshold" value={alertForm.threshold} onChange={e => setAlertForm({...alertForm, threshold: parseInt(e.target.value) || ''})}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Create</button>
                </form>
              </div>
              {alerts.length > 0 ? (
                <div className="bg-white rounded-lg shadow divide-y">
                  {alerts.map(a => (
                    <div key={a.id} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">{a.name}</span>
                        <span className="text-sm text-gray-500 ml-2">{a.metric} {a.conditionType} {a.threshold}</span>
                        {a.enabled && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded ml-2">Active</span>}
                        {a.lastTriggeredAt && <span className="text-xs text-gray-400 ml-2">Last: {new Date(a.lastTriggeredAt).toLocaleDateString()}</span>}
                      </div>
                      <button onClick={async () => { await api(`/analytics/sites/${id}/alerts/${a.id}`, {method: 'DELETE'}); load(); }}
                        className="text-sm text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-sm">No alerts configured.</p>}
            </div>
          )}
        </>
      )}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEdit(false)}>
          <form onSubmit={async e => {
            e.preventDefault();
            const res = await api(`/analytics/sites/${id}`, { method: 'PUT', body: JSON.stringify({ name: editName.trim(), domain: editDomain.trim() || null }) });
            if (res.ok) { setShowEdit(false); load(); }
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Site</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                <input autoFocus type="text" value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                <input type="text" value={editDomain} onChange={e => setEditDomain(e.target.value)}
                  placeholder="example.com" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowEdit(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!editName.trim()} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function StatTable({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-sm font-medium text-gray-500 mb-3">{title}</h3>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-gray-700 truncate mr-4">{item.name || '(direct)'}</span>
            <span className="text-gray-500 font-medium flex-shrink-0">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, prev }) {
  let delta = null;
  if (prev != null && typeof value === 'number' && prev > 0) {
    delta = Math.round((value - prev) / prev * 100);
  } else if (prev != null && typeof value === 'number' && prev === 0 && value > 0) {
    delta = 100;
  }
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {delta != null && (
        <p className={`text-xs font-medium mt-1 ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-400'}`}>
          {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {Math.abs(delta)}% vs prev period
        </p>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

const PAGE_COLORS = ['#d97706', '#2563eb', '#16a34a', '#dc2626', '#7c3aed'];

function buildPageTrendData(pageviewsByPage) {
  const dateMap = {};
  pageviewsByPage.forEach(pt => {
    (pt.data || []).forEach(d => {
      if (!dateMap[d.date]) dateMap[d.date] = {date: d.date};
      dateMap[d.date][pt.page] = d.count;
    });
  });
  return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
}

function VisitorSessions({ pageviews }) {
  const sessions = {};
  pageviews.forEach(pv => {
    const key = pv.visitorId || pv.id;
    if (!sessions[key]) sessions[key] = [];
    sessions[key].push(pv);
  });
  const sorted = Object.entries(sessions)
    .map(([vid, pvs]) => ({ visitorId: vid, pageviews: pvs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) }))
    .sort((a, b) => new Date(b.pageviews[0].createdAt) - new Date(a.pageviews[0].createdAt))
    .slice(0, 50);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="text-sm font-medium text-gray-500">Recent Visitor Sessions ({sorted.length})</h3>
      </div>
      <div className="divide-y">
        {sorted.map(session => {
          const first = session.pageviews[0];
          const last = session.pageviews[session.pageviews.length - 1];
          const dur = session.pageviews.length > 1 ? Math.round((new Date(last.createdAt) - new Date(first.createdAt)) / 1000) : 0;
          return (
            <div key={session.visitorId} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-mono text-xs text-gray-400">{session.visitorId.slice(0, 8)}</span>
                  {first.country && <span className="text-gray-500">{first.country}{first.city ? `, ${first.city}` : ''}</span>}
                  {first.browser && <span className="text-gray-400">{first.browser}</span>}
                  {first.deviceType && <span className="text-gray-400">{first.deviceType}</span>}
                  {first.utmCampaign && <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{first.utmCampaign}</span>}
                </div>
                <div className="text-xs text-gray-400">
                  {session.pageviews.length} page{session.pageviews.length !== 1 ? 's' : ''}
                  {dur > 0 && <span className="ml-2">{formatDuration(dur)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {session.pageviews.map((pv, i) => {
                  const prevTime = i > 0 ? new Date(session.pageviews[i-1].createdAt) : null;
                  const curTime = new Date(pv.createdAt);
                  const gap = prevTime ? Math.round((curTime - prevTime) / 1000) : 0;
                  return (
                    <span key={pv.id} className="flex items-center gap-1">
                      {i > 0 && <span className="text-gray-300 text-xs">{gap > 0 ? `${formatDuration(gap)} →` : '→'}</span>}
                      <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{pv.path}</span>
                      {pv.loadTimeMs > 0 && <span className="text-xs text-gray-400">{pv.loadTimeMs}ms</span>}
                    </span>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">{new Date(first.createdAt).toLocaleString()}</p>
            </div>
          );
        })}
        {sorted.length === 0 && <div className="text-center py-8 text-gray-400">No sessions found</div>}
      </div>
    </div>
  );
}

function HeatmapTab({ siteId, days, clickStats, setClickStats, clickPath, setClickPath, topPages }) {
  const loadClicks = useCallback((path) => {
    const params = path ? `path=${encodeURIComponent(path)}&days=${days}` : `days=${days}`;
    api(`/analytics/sites/${siteId}/clicks?${params}`).then(r => r.ok ? r.json() : null).then(setClickStats);
  }, [siteId, days, setClickStats]);

  useEffect(() => { loadClicks(clickPath); }, [loadClicks, clickPath]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Click Heatmap</h3>
      <div className="flex gap-2 mb-4">
        <select value={clickPath} onChange={e => setClickPath(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All pages</option>
          {topPages?.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      {clickStats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <StatCard label="Total Clicks" value={clickStats.totalClicks} />
          </div>
          {clickStats.clicksByPage?.length > 0 && !clickPath && (
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <StatTable title="Clicks by Page" items={clickStats.clicksByPage} />
            </div>
          )}
          {clickStats.topElements?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Most Clicked Elements</h4>
              <div className="space-y-2">
                {clickStats.topElements.map((el, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">{el.tag}</span>
                      <span className="text-gray-700 truncate max-w-xs">{el.text || el.selector || '(no text)'}</span>
                    </div>
                    <span className="text-gray-500 font-medium">{el.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {clickStats.clicks?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Click Density Map</h4>
              <div className="relative bg-gray-50 border rounded" style={{height: 400}}>
                {clickStats.clicks.map((c, i) => {
                  const maxCount = clickStats.clicks[0]?.count || 1;
                  const opacity = Math.max(0.2, c.count / maxCount);
                  const size = Math.max(12, Math.min(40, (c.count / maxCount) * 40));
                  return (
                    <div key={i} className="absolute rounded-full" title={`${c.count} clicks`}
                      style={{left: `${c.xPct}%`, top: `${Math.min(c.yPct, 95)}%`, width: size, height: size,
                        backgroundColor: `rgba(239, 68, 68, ${opacity})`, transform: 'translate(-50%, -50%)',
                        border: '1px solid rgba(239, 68, 68, 0.3)'}} />
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">Dot size and opacity represent click frequency</p>
            </div>
          )}
          {(!clickStats.topElements?.length && !clickStats.clicks?.length && !clickStats.clicksByPage?.length) && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p>No click data recorded yet. The tracking snippet automatically captures clicks.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CohortTab({ siteId, days, cohortData, setCohortData }) {
  useEffect(() => {
    api(`/analytics/sites/${siteId}/cohorts?weeks=8`).then(r => r.ok ? r.json() : null).then(setCohortData);
  }, [siteId, setCohortData]);

  if (!cohortData) return <div className="text-gray-500">Loading cohort data...</div>;
  const cohorts = cohortData.cohorts || [];
  if (cohorts.length === 0) return <div className="text-gray-500">Not enough data for cohort analysis.</div>;

  const maxWeeks = Math.max(...cohorts.map(c => c.retention?.length || 0));

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Cohort Retention</h3>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cohort</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">New</th>
              {Array.from({length: maxWeeks}, (_, i) => (
                <th key={i} className="px-3 py-2 text-center text-xs font-medium text-gray-500">Wk {i}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {cohorts.map(c => (
              <tr key={c.week}>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{c.week}</td>
                <td className="px-3 py-2 text-gray-700">{c.newVisitors}</td>
                {Array.from({length: maxWeeks}, (_, i) => {
                  const val = c.retention?.[i];
                  const opacity = val != null ? Math.max(val / 100, 0.05) : 0;
                  return (
                    <td key={i} className="px-3 py-2 text-center" style={{backgroundColor: val != null ? `rgba(217, 119, 6, ${opacity})` : 'transparent'}}>
                      {val != null ? `${val}%` : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
